import { memo, useCallback, useEffect, useRef } from 'react';

type SmartSearchPromotionDropdownProps = {
  onLearnMoreClick: () => void;
};

const SMART_SEARCH_EXAMPLE_SEARCH_PHRASES = [
  'videos of dogs running',
  'cats chasing laser pointers',
  'happy faces wearing colorful hats',
  'children playing in the park',
  'sports action shots',
  'beautiful dance performances',
];

export const SmartSearchPromotionDropdown = memo(({ onLearnMoreClick }: SmartSearchPromotionDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pillContainerRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);

  const updatePillContainerDimensions = useCallback(() => {
    if (pillContainerRef.current && containerRef.current && parentContainerRef.current) {
      const pillContainerBounds = pillContainerRef.current.getBoundingClientRect();

      if (pillContainerBounds.width > 0) {
        pillContainerRef.current.style.setProperty(
          '--pill-left-offset',
          `calc(50% - ${pillContainerBounds.width / 2}px)`,
        );

        if (pillContainerBounds.height > 0) {
          const computedStyle = getComputedStyle(parentContainerRef.current);
          const paddingTop = parseFloat(computedStyle.paddingTop);
          const paddingBottom = parseFloat(computedStyle.paddingBottom);
          const totalPadding = paddingTop + paddingBottom;

          const totalHeight = Math.max(totalPadding, pillContainerBounds.height + totalPadding);
          parentContainerRef.current.style.setProperty('--parent-container-height', `${totalHeight}px`);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        updatePillContainerDimensions();
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updatePillContainerDimensions]);

  return (
    <div
      data-theme="light"
      className="relative size-full grow overflow-y-auto rounded-8 border border-grey-5 bg-grey-1"
      ref={containerRef}
    >
      <div className="inline-flex w-full flex-col items-start justify-start overflow-hidden rounded-8 outline outline-1 outline-grey-5 [background:linear-gradient(180deg,#E4DEFC_50%,#FFF_85%,#FFF_100%)]">
        <div
          className="pointer-events-none relative self-stretch rounded-[7px] border border-grey-5 [height:var(--parent-container-height,auto)]"
          ref={parentContainerRef}
        >
          <div
            className="absolute -top-[16px] inline-flex w-[600px] flex-wrap place-content-center items-center gap-2 [left:var(--pill-left-offset,50%)]"
            ref={pillContainerRef}
          >
            {SMART_SEARCH_EXAMPLE_SEARCH_PHRASES.map((pill) => (
              <div
                className="relative flex items-center justify-center rounded-[100px] bg-grey-1/40 px-4 py-2"
                key={pill}
              >
                <div className="pointer-events-none absolute inset-0 rounded-[100px] border border-grey-1 [mask-composite:intersect] [mask-image:radial-gradient(circle_at_100%_0%,transparent_7.5%,white_15%),radial-gradient(circle_at_0%_100%,transparent_7.5%,white_15%)]"></div>
                <div className="justify-center font-semibold text-14 text-purple-10 leading-tight">
                  &ldquo;{pill}&rdquo;
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-2 self-stretch overflow-hidden px-4 pt-2 pb-4">
          <div className="flex w-[340px] flex-col gap-2">
            <div className="flex flex-col items-center justify-center gap-1 self-stretch">
              <div className="inline-flex items-center justify-center self-stretch">
                <div className="justify-center">
                  <span className="inline font-control-upright font-normal text-[19px] text-grey-12 leading-[20px] tracking-tight">
                    Search how you <span className="font-control tracking-[1px]">think.</span>
                  </span>
                </div>
              </div>
              <div className="justify-center self-stretch text-center font-normal text-14 text-grey-12 leading-[20px] tracking-tight">
                Smart search expands your search using AI to find exactly what you&rsquo;re looking for&mdash;like
                magic.
              </div>
              <div className="flex flex-col items-start justify-start rounded">
                <button
                  className="inline-flex items-center justify-center gap-1 rounded bg-grey-1 px-2 py-[3px] text-center font-semibold text-12 text-grey-12 leading-[18px] tracking-tight outline outline-1 outline-grey-5 outline-offset-[-1px] hover:bg-grey-3"
                  onClick={onLearnMoreClick}
                  type="button"
                >
                  Learn more
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SmartSearchPromotionDropdown.displayName = 'SmartSearchPromotionDropdown';
